import * as React from 'react';
import {
  Box, Button, Grid, IconButton, Paper, Typography,
} from '@material-ui/core';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import type { GridColDef } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useNavigate } from 'react-router-dom';
import globalUseStyles from '../../../../globalClasses';
import type { IWorkView } from '../WorkViewInterfaces';
import WorkViewActionMenu from './WorkViewActionMenu';

const AllWorkViews: React.FC = () => {
  const globalStyles = globalUseStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [workViewId, setWorkViewId] = React.useState<string | null>(null);

  const workViewsFetcher = (url: string) => axios.get<IWorkView[]>(url)
    .then((response) => response.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(`There was the following error when fetching the Work Views: ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
    });

  const { data, mutate } = useSWR(
    ['/workView/GetWorkViews'],
    (url) => workViewsFetcher(url),
  );

  const handleActionMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      setWorkViewId(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setWorkViewId(null);
    setAnchorEl(null);
  };

  const handleDelete = () => {
    axios.post('/workView/deleteWorkView', { WorkViewId: workViewId })
      .then(() => {
        mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        enqueueSnackbar('Work View deleted successfully.', { variant: 'success' });
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const displayVertIcon = (workViewIdParameter: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleActionMenuClick(e, workViewIdParameter)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const manualAddColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 1.5 },
    { field: 'layout', headerName: 'Layout Style', flex: 0.5 },
    {
      field: 'id',
      type: 'string',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  return (
    <Grid>
      <Grid container>
        <Grid container>
          <Grid item xs={10}>
            <Box pb={3} pt={3}>
              <Typography variant="h4" className={globalStyles.labelText}>{'Configure Work Views'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Box pb={3} pt={3}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => navigate('/create-WorkView')}
              >
                {'Create new Work View'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box pb={3}>
          <Paper elevation={4}>
            <Box p={3}>
              <Typography variant="h6">{'Existing Work Views'}</Typography>
            </Box>
            {data
              && (
                <>
                  <DataGrid
                    rows={data || []}
                    columns={manualAddColumns}
                    autoHeight
                    className={globalStyles.transparentDataGrid}
                  />
                  {
                    workViewId && anchorEl && (
                      <WorkViewActionMenu
                        workViewId={workViewId}
                        anchorEl={anchorEl}
                        handleClose={handleClose}
                        handleDelete={handleDelete}
                      />
                    )
                  }
                </>
              )}
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};

export default AllWorkViews;
